import { ConnectProto } from "@racoon/api";

const { NotificationEvent, NotificationChannel } = ConnectProto;
type NotificationEvent = ConnectProto.NotificationEvent;
type NotificationChannel = ConnectProto.NotificationChannel;
type UserRole = ConnectProto.UserRole;

/** Rows of the customer tab, grouped as in the design. */
export const CUSTOMER_EVENT_GROUPS = [
  {
    key: "jobs",
    events: [
      NotificationEvent.JOB_ASSIGNED,
      NotificationEvent.JOB_ENROUTE,
      NotificationEvent.JOB_COMPLETED,
    ],
  },
  { key: "requests", events: [NotificationEvent.JOB_CREATED] },
  {
    key: "contracts",
    events: [NotificationEvent.CONTRACT_EXPIRING, NotificationEvent.WORK_ACT_AND_INVOICE_ISSUED],
  },
] as const;

/** Column order of the table. */
export const CHANNEL_COLUMNS = [
  NotificationChannel.PUSH,
  NotificationChannel.WHATSAPP,
  NotificationChannel.EMAIL,
] as const;

export interface EventDraft {
  channels: NotificationChannel[];
  optOutAllowed: boolean;
}

/** One audience's rules by event. A missing event or an empty `channels` means "off". */
export type RuleDraft = ReadonlyMap<NotificationEvent, EventDraft>;

const OFF: EventDraft = { channels: [], optOutAllowed: false };

export function eventDraft(draft: RuleDraft, event: NotificationEvent): EventDraft {
  return draft.get(event) ?? OFF;
}

export function toDraft(rules: ConnectProto.NotificationRule[], audience: UserRole): RuleDraft {
  return new Map(
    rules
      .filter((rule) => rule.audience === audience)
      .map((rule) => [
        rule.event,
        { channels: [...rule.channels], optOutAllowed: rule.optOutAllowed },
      ]),
  );
}

/**
 * The full rule list to save: other audiences' rules untouched, and one rule per event of
 * `audience` that has a channel. The backend rejects a rule without channels, so an event
 * switched fully off is left out, which is how "off" is stored.
 */
export function toRules(
  allRules: ConnectProto.NotificationRule[],
  audience: UserRole,
  draft: RuleDraft,
): ConnectProto.NotificationRule[] {
  const kept = allRules.filter((rule) => rule.audience !== audience);
  const edited = [...draft]
    .filter(([, { channels }]) => channels.length > 0)
    .map(
      ([event, { channels, optOutAllowed }]): ConnectProto.NotificationRule => ({
        $typeName: "raccoonai.NotificationRule",
        event,
        audience,
        channels,
        optOutAllowed,
      }),
    );
  return [...kept, ...edited];
}

/** Turning on the first channel of an off event lets employees opt out by default. */
export function toggleChannel(
  draft: RuleDraft,
  event: NotificationEvent,
  channel: NotificationChannel,
): RuleDraft {
  const current = eventDraft(draft, event);
  const channels = current.channels.includes(channel)
    ? current.channels.filter((c) => c !== channel)
    : [...current.channels, channel];
  const optOutAllowed = current.channels.length === 0 ? true : current.optOutAllowed;
  return new Map(draft).set(event, { channels, optOutAllowed });
}

export function setOptOut(
  draft: RuleDraft,
  event: NotificationEvent,
  optOutAllowed: boolean,
): RuleDraft {
  return new Map(draft).set(event, { ...eventDraft(draft, event), optOutAllowed });
}
